import React from 'react';
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Chip,
  Typography,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '../../ui/BaseDialog';
import { useShareFolderDialog } from '../../../hooks/dialogs/useShareFolderDialog';
import { ShareFolderDialogProps } from './types';
import {
  StepUrlEntry,
  StepFileSelection,
  StepSharingSettings,
  StepNameAndDetails,
} from './components';

/**
 * ShareFolderDialog - Main container for folder sharing wizard
 *
 * Provides a multi-step wizard for creating or editing folder sync configurations:
 * - Step 1: URL entry and validation
 * - Step 2: File selection
 * - Step 3: Sharing settings
 * - Step 4: Name and details
 *
 * Supports both create and edit modes with different workflows
 */
export const ShareFolderDialog: React.FC<ShareFolderDialogProps> = (props) => {
  const { open, onClose } = props;
  const { t } = useTranslation();

  // Main orchestration hook
  const {
    isEditMode,
    formState,
    handleUrlChange,
    handleNameChange,
    handleDescriptionChange,
    handleSharingChange,
    handleFileToggle,
    handleSelectAll,
    handleSelectNone,
    handleValidateUrl,
    isValidating,
    detectProvider,
    activeStep,
    setActiveStep,
    handleSave,
    canProceedStep1,
    canProceedStep2,
    canSubmit,
    isLoading,
    dialogError,
    successMessage,
  } = useShareFolderDialog(props);

  const handleClose = () => {
    onClose();
  };

  const detectedProvider = detectProvider(formState.url);

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title={
        isEditMode ? t('sharedFolders.actions.edit') : t('sharedFolders.addFolder')
      }
      subtitle={
        isEditMode
          ? `${t('sharedFolders.actions.edit')}: ${props.initialData?.name}`
          : t('shareFolder.subtitle')
      }
      maxWidth="md"
      fullWidth
      loading={isLoading}
      error={dialogError}
      success={successMessage}
      DialogProps={{
        PaperProps: {
          sx: { minHeight: '70vh' }
        }
      }}
      actions={
        <>
          <Button
            onClick={handleClose}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          {activeStep === 3 && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!canSubmit}
              startIcon={
                formState.syncStatus === 'processing' ? (
                  <CircularProgress size={20} />
                ) : (
                  <CloudUploadIcon />
                )
              }
            >
              {formState.syncStatus === 'processing'
                ? (isEditMode ? t('shareFolder.updating') : t('shareFolder.importing'))
                : (isEditMode ? t('common.save') : t('shareFolder.importFiles'))
              }
            </Button>
          )}
        </>
      }
    >
      <Box sx={{ mt: 1 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {/* Step 1: Enter Link */}
          <Step>
            <StepLabel
              optional={
                formState.url && (
                  <Chip
                    size="small"
                    label={detectedProvider}
                    color="primary"
                    variant="outlined"
                  />
                )
              }
            >
              {t('shareFolder.steps.enterLink')}
            </StepLabel>
            <StepContent>
              <StepUrlEntry
                url={formState.url}
                onChange={handleUrlChange}
                onValidate={handleValidateUrl}
                isValidating={isValidating}
                provider={detectedProvider}
                error={formState.errorMessage}
              />
            </StepContent>
          </Step>

          {/* Step 2: Select Files */}
          <Step>
            <StepLabel
              optional={
                isEditMode ? (
                  <Typography variant="caption">{t('shareFolder.modifySelection')}</Typography>
                ) : (
                  <Typography variant="caption">Optional</Typography>
                )
              }
            >
              {isEditMode ? t('shareFolder.editFileSelection') : t('shareFolder.steps.selectFiles')}
            </StepLabel>
            <StepContent>
              <StepFileSelection
                folderInfo={formState.folderInfo}
                files={formState.files}
                selectedFiles={formState.selectedFiles}
                onToggleFile={handleFileToggle}
                onSelectAll={handleSelectAll}
                onSelectNone={handleSelectNone}
                isLoading={formState.syncStatus === 'fetching'}
                mode={props.mode}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button onClick={() => setActiveStep(2)} variant="contained">
                  {t('shareFolder.continue')}
                </Button>
                {!isEditMode && (
                  <Button onClick={() => setActiveStep(0)}>
                    {t('shareFolder.back')}
                  </Button>
                )}
              </Box>
            </StepContent>
          </Step>

          {/* Step 3: Sharing Settings */}
          <Step>
            <StepLabel>{t('shareFolder.steps.sharingSettings')}</StepLabel>
            <StepContent>
              <StepSharingSettings
                sharingData={formState.sharingData}
                onChange={handleSharingChange}
                mode={props.mode}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button onClick={() => setActiveStep(3)} variant="contained">
                  {t('shareFolder.continue')}
                </Button>
                <Button onClick={() => setActiveStep(1)}>
                  {t('shareFolder.back')}
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 4: Name & Details */}
          <Step>
            <StepLabel>{t('shareFolder.steps.nameAndImport')}</StepLabel>
            <StepContent>
              <StepNameAndDetails
                url={formState.url}
                name={formState.name}
                description={formState.description}
                onChangeName={handleNameChange}
                onChangeDescription={handleDescriptionChange}
                isProcessing={formState.syncStatus === 'processing'}
                mode={props.mode}
              />
            </StepContent>
          </Step>
        </Stepper>
      </Box>
    </BaseDialog>
  );
};
